import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  Stack,
  Typography,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  CircularProgress,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { useNavigate } from "react-router-dom";
import {
  useGetPermissionMatrixModulesQuery,
  useCreateUserRoleMutation,
  useUpdateRolePermissionMutation,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import PermissionMatrix from "../Components/PermissionMatrix";
import useBeforeUnload from "../Components/useBeforeUnload";
import { useConfirm } from "../../../Components/Confirm";
import {
  MatrixGroup,
  MatrixFlag,
  TEMPLATE_OPTIONS,
  TemplateKey,
  getTemplatePermissionIds,
} from "../Components/permissionUtils";

const NewRole: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const { data: modulesResp, isLoading: modulesLoading } =
    useGetPermissionMatrixModulesQuery();
  const confirm = useConfirm();
  const [createUserRole, { isLoading: creating }] = useCreateUserRoleMutation();
  const [updateRolePermission, { isLoading: assigning }] =
    useUpdateRolePermissionMutation();
  const saving = creating || assigning;

  const groups: MatrixGroup[] = (modulesResp as any)?.data?.groups ?? [];
  const flags: MatrixFlag[] = (modulesResp as any)?.data?.flags ?? [];

  const [roleName, setRoleName] = useState("");
  const [template, setTemplate] = useState<TemplateKey>("custom");
  const [assigned, setAssigned] = useState<Set<number>>(new Set());

  const dirty = roleName.trim().length > 0 || assigned.size > 0;
  useBeforeUnload(dirty && !saving);

  const applyTemplate = (key: TemplateKey) => {
    setTemplate(key);
    setAssigned(new Set(getTemplatePermissionIds(key, groups, flags)));
  };

  const handleCancel = async () => {
    if (dirty) {
      const ok = await confirm({
        title: "Discard new role?",
        message: "Discard this new role and its unsaved changes?",
        confirmText: "Discard",
        cancelText: "Keep editing",
        variant: "warning",
      });
      if (!ok) return;
    }
    navigate("/user/role/list");
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      showErrorMessage("Role name is required.");
      return;
    }
    if (assigned.size === 0) {
      const ok = await confirm({
        title: "Create role with no permissions?",
        message: "This role has no permissions granted. Users assigned to it will have no access. Create anyway?",
        confirmText: "Create anyway",
        cancelText: "Go back",
        variant: "warning",
      });
      if (!ok) return;
    }
    try {
      const res: any = await createUserRole({
        roleName: toProperCase(roleName),
        privileges: [],
      }).unwrap();
      if (res?.status === false) {
        showErrorMessage(res.message || "Failed to create role.");
        return;
      }
      const newId = Number(res?.data?.id);
      if (!newId) {
        showErrorMessage("Role created but ID missing — please refresh.");
        return;
      }
      if (assigned.size > 0) {
        const permRes: any = await updateRolePermission({
          roleId: newId,
          permissionIds: Array.from(assigned),
        }).unwrap();
        if (permRes?.status === false) {
          showErrorMessage(permRes.message || "Role created but permissions failed to save.");
          navigate(`/user/role/${newId}/edit`);
          return;
        }
      }
      showMessage("Role created successfully.");
      navigate(`/user/role/${newId}`);
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Something went wrong.");
    }
  };

  return (
    <Box sx={{ background: "#f8fafc", minHeight: "100%" }}>
      {/* Header */}
      <Card
        sx={{
          borderRadius: 0,
          boxShadow: "none",
          borderBottom: "1px solid #e2e8f0",
          background: "#fff",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ px: 3, py: 2 }}
        >
          <IconButton onClick={handleCancel} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
              Add Role
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Create a new role and pick which permissions it grants.
            </Typography>
          </Box>
        </Stack>
      </Card>

      <Box sx={{ p: 3, pb: 12 }}>
        {/* Role name + template */}
        <Box
          sx={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
            Role Name <span style={{ color: "#dc2626" }}>*</span>
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="e.g. Front Desk Manager"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            sx={{ maxWidth: 420 }}
          />
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
              Permission Template
            </Typography>
            <RadioGroup
              row
              value={template}
              onChange={(e) => applyTemplate(e.target.value as TemplateKey)}
            >
              {TEMPLATE_OPTIONS.map((t) => (
                <FormControlLabel
                  key={t.key}
                  value={t.key}
                  control={<Radio size="small" />}
                  label={
                    <Typography sx={{ fontSize: 13 }}>{t.label}</Typography>
                  }
                />
              ))}
            </RadioGroup>
          </Box>
        </Box>

        <PermissionMatrix
          groups={groups}
          flags={flags}
          assigned={assigned}
          onChange={setAssigned}
          loading={modulesLoading}
        />
      </Box>

      {/* Sticky footer */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#fff",
          borderTop: "1px solid #e2e8f0",
          px: 3,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          boxShadow: "0 -2px 10px rgba(15,23,42,0.05)",
        }}
      >
        <Typography sx={{ fontSize: 13, color: "#64748b", flex: 1 }}>
          {assigned.size === 0
            ? "No permissions selected."
            : `${assigned.size} permission${assigned.size === 1 ? "" : "s"} selected.`}
        </Typography>
        <Button onClick={handleCancel} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || modulesLoading || !roleName.trim()}
          startIcon={
            saving ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              <SaveIcon />
            )
          }
        >
          Create Role
        </Button>
      </Box>
    </Box>
  );
};

export default NewRole;
